import { map } from "rxjs/operators";
import { AfterViewInit, Component, HostBinding } from "@angular/core";
import { Observable } from "rxjs";

import { Conversion, ErrorClass, Fiat, NotFoundError, Price, ValidationError } from "@models";

import { FiatService } from "@main-services/currency/fiat.service";
import { AccountService } from "@modules/account/services/account.service";

import { StepSummaryModal } from "../step-summary.modal";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { ModalHeader } from "@models/modal_step";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Tradable } from "@models/interfaces/tradable";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { BpModalHasSave } from "@models/interfaces/bp-modal-has-save";
import { stripFunnyInput } from "@helpers/utils";
import { convertFiatToTradable, convertTradableToFiat } from "@helpers/tradable-convert";
import { TradableTransaction } from "@models/transaction";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { Modal } from "@core-services/modal.service";
import { InfoService } from "@core-services/info.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";

@Component({
	templateUrl: "./transfer-modal.component.html",
	standalone: false
})
export class TransferModalComponent extends StepSummaryModal implements AfterViewInit, BpModalHasSave {
	protected category: string = "transfer";

	protected label: string = "transfer";
	protected fallback: Modal = null;

	public price: Price = null;
	public userWants = "fiat";

	protected isMax = false;

	@HostBinding("class") public class = "modal-steps";

	public get isFirstStep(): boolean {
		return (this.getWallet() == null && this.isEdit(0)) || this.isEdit(1);
	}

	public get isSecondStep(): boolean {
		return (this.getWallet() != null && this.getSecondWallet() == null && this.isEdit(0)) || this.isEdit(2);
	}

	public get isThirdStep(): boolean {
		return (this.getWallet() != null && this.getSecondWallet() != null && this.isEdit(0)) || this.isEdit(3);
	}

	public constructor(
		private apicallService: ApicallService,
		private infoService: InfoService,
		private tradableListService: TradableListService,
		protected fiatService: FiatService,
		protected accountService: AccountService,
		private walletListService: TradableWalletListService,
		protected confirmModalService: ConfirmModalService
	) {
		super(confirmModalService);
	}

	public ngAfterViewInit(): void {
		if (this.data.hasOwnProperty("preselected")) {
			this.preselected = this.data.preselected;
			if (this.preselected.getBalance() === 0) {
				this.preselected = null;
			}
			delete this.data["preselected"];
		}
		if (this.data.from != null) {
			this.selectSenderWallet(this.walletListService.getItemById(this.data.from));
		}

		this.modalService.getCurrentModal().onDismiss.subscribe((inputs) => {
			if (this.fallback && !(inputs.data && inputs.data.goToFallback)) {
				this.fallback.instance.saved = true; // Parent modal must have 'true' canModalDismiss() to skip second "OK / Cancel"
				this.fallback.dismiss();
			}
		});

		super.ngAfterViewInit();
	}

	public getFiat(): Fiat {
		return this.accountService.getDefaultFiat();
	}

	public selectSenderWallet(w: TradableWallet): void {
		this.data.from = w.getId();
		this.apicallService
			.post("wallet/" + w.getId() + "/limits", new ApicallOption(Price, { type: "internal" }))
			.pipe(map((p) => p[0]))
			.subscribe(
				(a) => {
					this.price = a;
					this.doTrapFocusTime();
				},
				(e) => {
					if (e instanceof ErrorClass) {
						this.infoService.setError(e);
					} else {
						console.error(e);
					}
				}
			);
	}

	public selectReceiverWallet(w: TradableWallet): void {
		this.data.to = w.getId();
		this.doTrapFocusTime();
	}

	public getItem(): Tradable {
		return this.tradableListService.getItemById(this.getWallet().getCurrencyId());
	}

	public getWallet(): TradableWallet {
		return this.walletListService.getItemById(this.data.from);
	}

	public getSecondWallet(): TradableWallet {
		return this.walletListService.getItemById(this.data.to);
	}

	public getWallets(): TradableWallet[] {
		return (
			this.walletListService
				.getWalletsByTradableId(this.getWallet().getCurrencyId())
				.filter((w) => w.getBalance() > 0) || []
		);
	}

	public getNonEmptyWallets(): TradableWallet[] {
		return this.walletListService.getNonEmptyWallets();
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.directTransfer) {
			return true;
		}
		return super.modalCanDismiss();
	}

	public disabled(w: TradableWallet | Tradable): boolean {
		if (w instanceof TradableWallet) {
			return w.getBalance() === 0;
		}
		return false;
	}

	public availableWallets(): TradableWallet[] {
		let ws = [];

		this.tradableListService.list.forEach((c) => {
			const tradableWallets = this.walletListService.getWalletsByTradableId(c.getId());
			if (tradableWallets.length >= 2 && tradableWallets.some((w) => w.getBalance() > 0)) {
				ws = ws.concat(tradableWallets);
			}
		});
		return ws;
	}

	public stepBack(): void {
		this.data.to = null;
	}

	public getStepsFromOfferFiat(): number {
		return this.fiatService.stepSizeByItem(this.getFiat());
	}

	public loading: boolean = false;

	public save(): void {
		if (!this.getSufficientFunds()) {
			this.infoService.setError(this.translationService.translate("transaction_amount_low"));
			return;
		}

		this.loading = true;
		this.data.clearError();
		const a = new ApicallOption(TradableTransaction, this.data);
		this.apicallService.post("wallet/" + this.data.from + "/transfer", a).subscribe(
			() => {
				this.loading = false;
				this.walletListService.serviceForId(this.getWallet().getId()).fetchWallets();
				this.walletListService.serviceForId(this.getWallet().getId()).transactionReceived();
				this.saved = true;
				this.walletListService.services.forEach((s) => s.transactionReceived());
				this.done();
			},
			(error) => {
				this.loading = false;
				if (error instanceof NotFoundError) {
					this.data.setError("recipient", "not valid");
				} else if (error instanceof ValidationError) {
					this.data.setValidationErrors(error);
					this.infoService.setError(error);
				} else {
					console.error(error);
				}
			}
		);
	}

	public otherWallets(): TradableWallet[] {
		return (
			this.walletListService
				.getWalletsByTradableId(this.getWallet().getCurrencyId())
				.filter((w) => w.getId() !== this.getWallet().getId()) || []
		);
	}

	public amountItemTotal(ignore: boolean = true): string {
		const value = Math.min(+this.data.getAmountTradable(), this.getWallet().getBalance());
		return isNaN(value) ? "0" : "" + value;
	}

	// TODO: eslint fix
	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public get amount(): any {
		return this.userWants === "cryptocoin" ? this.data.getAmountTradable() : this.data.getAmountFiat();
	}

	/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
	public set amount(n: any) {
		if (n === 0) {
			return;
		}

		n = stripFunnyInput(n);
		if (this.userWants === "cryptocoin") {
			this.data.setAmountTradable(n);
			this.data.setAmountFiat("" + this.toFiatAmount(n));
		} else {
			this.data.setAmountFiat(n);
			this.data.setAmountTradable("" + this.toItemAmount(n));
		}
	}

	public inputOnFocus(e: FocusEvent): void {
		this.trackSelect("amount_input");
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.getTitle());

		if (this.isThirdStep && this.isEdit(0)) {
			header.left = "back";
		} else if (this.isSecondStep && this.isEdit(2)) {
			header.left = "close";
		} else if (this.isFirstStep && (!this.isEdit(0) || !this.isEdit(1))) {
			header.left = "abort";
		}
		if (
			(this.isFirstStep && (this.isEdit(1) || (this.isEdit(0) && !!this.preselected))) ||
			(this.isSecondStep && this.isEdit(2))
		) {
			header.right = "ok";
		} else if (this.isFirstStep && !this.isEdit(0) && !this.isEdit(1)) {
			header.right = "abort";
		} else if ((this.isSecondStep && !this.isEdit(2)) || this.isThirdStep) {
			header.right = "close";
		}

		return header;
	}

	public onHeaderButtonClick(name: string): void {
		if (name === "back") {
			this.stepBack();
		} else if (name === "abort" || name === "close") {
			this.close();
		} else if (name === "ok" && this.isEdit(0) && !!this.preselected) {
			this.selectSenderWallet(this.preselected as TradableWallet);
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public getTitle(): string {
		switch (this.stepNumber) {
			case 1:
				return this.translate("send_choose_wallet");
			case 2:
				return this.translate("wallet_to_wallet_header", [this.getItem().getName()]);
			case 3:
				return this.translate("wallet_to_wallet_header", [this.getItem().getName()]);
			default:
				return "";
		}
	}

	public getAmounts(): Conversion[] {
		if (this.price == null) {
			return [];
		}
		return this.price.getAmounts().slice(0, 2);
	}

	public maxAmount(): Conversion {
		if (this.price == null) {
			return new Conversion();
		}
		return this.price.getMaxAmount();
	}

	public getMultiplyTradableAmountByFiatPrice(): number {
		if (this.userWants === "fiat") {
			return this.amount;
		}
		if (!this.price || !this.price.getPrice()) {
			return 0;
		}
		const fiatPrice = this.price.getPrice().getFiat();
		return fiatPrice ? +this.amountItemTotal() * +fiatPrice : 0;
	}

	public getIcon(): string {
		return this.userWants === "cryptocoin"
			? this.getItem().getIconCssName(true)
			: `bpi__asset-${this.getFiat().getSymbol().toLowerCase()}`;
	}

	public getInputFormat(): string {
		return this.userWants;
	}

	public change(type: string = "fiat"): void {
		if (type === "fiat" || type === "cryptocoin") {
			this.userWants = type;
		} else if (type === "swap") {
			if (this.userWants === "fiat") {
				this.change("cryptocoin");
			} else {
				this.change("fiat");
			}
		}
		this.isMax = false;
	}

	public setAmount(n: Conversion): void {
		if (+n.tradableAmount() === +this.maxAmount().tradableAmount()) {
			this.setMaxAmount();
		} else {
			this.userWants = "fiat";
			this.amount = n.fiatAmount();
			this.track("amount");
		}
	}

	public setMaxAmount(): void {
		this.userWants = "cryptocoin";
		this.amount = this.maxAmount().tradableAmount();
		this.track("amount");
	}

	public getSufficientFunds(): boolean {
		let num = this.amountItemTotal();

		num = stripFunnyInput(num);

		return +num <= this.getWallet().getBalance() && +num > 0;
	}

	public toItemAmount(fiatAmount: number): number {
		return convertFiatToTradable(this.getFiat(), fiatAmount, this.getItem());
	}

	public toFiatAmount(coinAmount: number): number {
		return convertTradableToFiat(this.getItem(), coinAmount, this.getFiat());
	}
}
